'use client';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';

// ── Compact animated ticker bar ───────────────────────────────────────────────
export const OffersAlertBar = ({ offers }) => {
    const [activeIdx, setActiveIdx] = useState(0);
    const [sliding,   setSliding]   = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const taglines = buildTaglines(offers);
    const total    = taglines.length;

    useEffect(() => {
        if (total <= 1) return;
        const id = setInterval(() => {
            setSliding(true);
            setTimeout(() => {
                setActiveIdx(prev => (prev + 1) % total);
                setSliding(false);
            }, 320);
        }, 2600);
        return () => clearInterval(id);
    }, [total]);

    if (!offers || total === 0) return null;

    const current = taglines[activeIdx];

    return (
        <>
            <div
                style={{
                    background: 'var(--primary-color)',
                    borderBottom: '1px solid rgba(255,255,255,0.12)',
                    height: 36,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    userSelect: 'none',
                }}
                onClick={() => setModalOpen(true)}
            >
                <div className="container flex align-items-center gap-2" style={{ height: '100%' }}>

                {/* Count pill */}
                <span
                    className="flex align-items-center gap-1 flex-shrink-0 border-round px-2 text-xs font-bold"
                    style={{
                        background: 'rgba(255,255,255,0.18)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        color: '#fff',
                        height: 22,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <i className="pi pi-tag" style={{ fontSize: '0.6rem' }} />
                    {total} offer{total !== 1 ? 's' : ''}
                </span>

                {/* Animated tagline */}
                <div style={{ flex: 1, overflow: 'hidden', height: 20, position: 'relative' }}>
                    <span
                        key={activeIdx}
                        style={{
                            position: 'absolute',
                            left: 0,
                            lineHeight: '20px',
                            color: '#fff',
                            fontSize: '0.78rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            willChange: 'transform, opacity',
                            animation: sliding ? 'offerOut 0.32s ease forwards' : 'offerIn 0.32s ease forwards',
                        }}
                    >
                        <i className={`pi ${current.icon} mr-1`} style={{ fontSize: '0.68rem' }} />
                        {current.text}
                    </span>
                </div>

                {/* CTA */}
                <span
                    className="flex align-items-center gap-1 flex-shrink-0 text-xs font-semibold"
                    style={{ color: 'rgba(255,255,255,0.82)', whiteSpace: 'nowrap' }}
                >
                    Explore deals
                    <i className="pi pi-chevron-right" style={{ fontSize: '0.58rem' }} />
                </span>
                </div>
            </div>

            {modalOpen && <OffersModal offers={offers} onClose={() => setModalOpen(false)} />}
        </>
    );
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildTaglines(offers) {
    if (!offers) return [];
    const list = [];
    if (offers.prepaidDiscount?.enabled)
        list.push({ text: offers.prepaidDiscount.label, icon: 'pi-credit-card' });
    if (offers.cod?.allowed)
        list.push({ text: offers.cod.label, icon: 'pi-truck' });
    (offers.coupons?.items || []).forEach(c => {
        const disc  = c.discountType === 'flat' ? `₹${c.discountValue} off` : `${c.discountValue}% off`;
        const scope = c.scope === 'product'
            ? ` on ${c.products?.[0]?.title || 'select products'}`
            : c.scope === 'category'
            ? ` on ${c.categories?.[0]?.title || 'select categories'}`
            : '';
        list.push({ text: `Use ${c.code} · ${disc}${scope}`, icon: 'pi-ticket' });
    });
    return list;
}

function countActive(offers) {
    let n = 0;
    if (offers?.prepaidDiscount?.enabled) n++;
    if (offers?.cod?.allowed) n++;
    n += offers?.coupons?.items?.length || 0;
    return n;
}

// ── Modal ─────────────────────────────────────────────────────────────────────
const OffersModal = ({ offers, onClose }) => {
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const { prepaidDiscount, cod, coupons } = offers;

    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(11,49,99,0.55)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    width: '100%', maxWidth: '36rem',
                    maxHeight: '85vh',
                    borderRadius: 20,
                    boxShadow: '0 24px 64px rgba(11,49,99,0.4)',
                    display: 'flex', flexDirection: 'column',
                    background: '#f1f5f9',
                    overflow: 'hidden',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header (primary) ── */}
                <div
                    className="flex align-items-center justify-content-between px-4 py-3"
                    style={{ background: 'var(--primary-color)', flexShrink: 0 }}
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-tag" style={{ color: '#fbbf24', fontSize: '1rem' }} />
                        <span className="font-bold text-base" style={{ color: '#fff' }}>Available Offers</span>
                        <span
                            className="border-round px-2 py-1 text-xs font-bold"
                            style={{
                                background: 'rgba(255,255,255,0.18)',
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.3)',
                            }}
                        >
                            {countActive(offers)} active
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(255,255,255,0.15)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: 8, width: 30, height: 30,
                            cursor: 'pointer', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', color: '#fff',
                        }}
                    >
                        <i className="pi pi-times" style={{ fontSize: '0.75rem' }} />
                    </button>
                </div>

                {/* ── Amber hint strip ── */}
                <div
                    className="px-4 py-2"
                    style={{ background: '#f59e0b', color: '#fff', fontWeight: 500, flexShrink: 0 , fontSize:'0.8rem'}}
                >
                    <i className="pi pi-info-circle mr-1" style={{ fontSize: '0.8rem' }} />
                    Tap a coupon code to copy · Offers apply at checkout
                </div>

                {/* ── Scrollable body ── */}
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        flexDirection: 'column',
                        gap: '0.65rem',
                    }}
                >
                    {prepaidDiscount?.enabled && (
                        <StaticOfferCard
                            icon="pi-credit-card"
                            badge="PREPAID"
                            title={prepaidDiscount.label}
                            detail="Save more when you pay online via UPI, card, or net banking at checkout."
                        />
                    )}
                    {cod?.allowed && (
                        <StaticOfferCard
                            icon="pi-truck"
                            badge="COD"
                            title={cod.label}
                            detail="Pay in cash at your doorstep. ₹50 handling charge is added at checkout."
                        />
                    )}
                    {(coupons?.items || []).map((c, i) => (
                        <CouponCard key={c.code || i} coupon={c} onClose={onClose} />
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
};

// ── Static offer card (prepaid / COD) ─────────────────────────────────────────
const StaticOfferCard = ({ icon, badge, title, detail }) => (
    <div
        className="border-round-xl p-4 flex gap-3 mt-2"
        style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
    >
        <div
            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
            style={{ width: 42, height: 42, background: 'var(--primary-color)' }}
        >
            <i className={`pi ${icon}`} style={{ color: '#fff', fontSize: '1rem' }} />
        </div>
        <div style={{ flex: 1 }}>
            <span
                className="text-xs font-bold border-round px-2 py-1 mb-2 inline-block"
                style={{ background: '#f59e0b', color: '#fff', letterSpacing: '0.4px' }}
            >
                {badge}
            </span>
            <p className="m-0 font-bold text-sm" style={{ color: '#0f172a' }}>{title}</p>
            <p className="m-0 text-xs mt-1" style={{ color: '#64748b' }}>{detail}</p>
        </div>
    </div>
);

// ── Coupon card ───────────────────────────────────────────────────────────────
const CouponCard = ({ coupon, onClose }) => {
    const [copied, setCopied] = useState(false);

    const disc = coupon.discountType === 'flat'
        ? `Flat ₹${coupon.discountValue} off`
        : `${coupon.discountValue}% off`;

    const scopeLabel = coupon.scope === 'product'
        ? 'on select products'
        : coupon.scope === 'category'
        ? `on ${(coupon.categories || []).map(c => c.title).join(', ') || 'select categories'}`
        : 'on all products';

    const doCopy = () => {
        navigator.clipboard.writeText(coupon.code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div
            className="border-round-xl overflow-hidden mt-2"
            style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}
        >
            <div className="p-4 mt-2">
                <div className="flex align-items-start gap-3">
                    {/* Left: offer details */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex align-items-center gap-2 mb-1 flex-wrap">
                            <i className="pi pi-ticket" style={{ color: '#f59e0b', fontSize: '0.85rem' }} />
                            <span className="font-bold text-sm" style={{ color: '#0f172a' }}>{disc}</span>
                        </div>
                        <p className="m-0 text-xs mb-2" style={{ color: '#64748b' }}>{scopeLabel}</p>

                        {coupon.minOrderValue > 0 && (
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-info-circle" style={{ fontSize: '0.65rem', color: '#f59e0b' }} />
                                <span className="text-xs font-semibold" style={{ color: '#92400e' }}>
                                    Min. order ₹{coupon.minOrderValue}
                                </span>
                            </div>
                        )}

                        <p className="m-0 text-xs" style={{ color: '#94a3b8' }}>
                            {coupon.expiresAt
                                ? `Expires: ${new Date(coupon.expiresAt).toLocaleDateString('en-IN')}`
                                : 'No expiry · Use anytime'
                            }
                        </p>
                    </div>

                    {/* Right: copy chip */}
                    <button
                        onClick={doCopy}
                        title="Copy code"
                        style={{
                            background: copied ? '#f59e0b' : '#fffbeb',
                            border: `1.5px dashed ${copied ? '#f59e0b' : '#fbbf24'}`,
                            borderRadius: 10,
                            padding: '8px 14px',
                            cursor: 'pointer',
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3,
                            transition: 'all 0.2s',
                            minWidth: 82,
                        }}
                    >
                        <span style={{ fontSize: '0.85rem', fontWeight: 800, letterSpacing: '1.5px', color: copied ? '#fff' : '#92400e' }}>
                            {coupon.code}
                        </span>
                        <span style={{ fontSize: '0.58rem', fontWeight: 700, color: copied ? '#fff' : '#b45309' }}>
                            {copied ? 'COPIED!' : 'TAP TO COPY'}
                        </span>
                    </button>
                </div>
            </div>

            {/* Product links */}
            {coupon.scope === 'product' && coupon.products?.length > 0 && (
                <div
                    style={{
                        borderTop: '1px solid #f1f5f9',
                        padding: '8px 16px',
                        background: '#f8faff',
                        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                    }}
                >
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', flexShrink: 0 }}>Valid on:</span>
                    {coupon.products.map(p => (
                        <Link
                            key={p.slug}
                            href={`/${p.slug}`}
                            onClick={onClose}
                            className="no-underline flex align-items-center gap-1 border-round px-2 py-1"
                            style={{
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.72rem', fontWeight: 600,
                                color: 'var(--primary-color)',
                            }}
                        >
                            {p.coverImage && (
                                <img
                                    src={p.coverImage}
                                    alt={p.title}
                                    style={{ width: 18, height: 18, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }}
                                />
                            )}
                            {p.title}
                            <i className="pi pi-arrow-right" style={{ fontSize: '0.52rem', opacity: 0.6 }} />
                        </Link>
                    ))}
                </div>
            )}

            {/* Category links */}
            {coupon.scope === 'category' && coupon.categories?.length > 0 && (
                <div
                    style={{
                        borderTop: '1px solid #f1f5f9',
                        padding: '8px 16px',
                        background: '#f8faff',
                        display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                    }}
                >
                    <span style={{ fontSize: '0.68rem', color: '#94a3b8', flexShrink: 0 }}>Valid on:</span>
                    {coupon.categories.map(cat => (
                        <Link
                            key={cat.slug || cat._id}
                            href={`/category/${cat.slug}`}
                            onClick={onClose}
                            className="no-underline flex align-items-center gap-1 border-round px-2 py-1"
                            style={{
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.72rem', fontWeight: 600,
                                color: 'var(--primary-color)',
                            }}
                        >
                            {cat.title}
                            <i className="pi pi-arrow-right" style={{ fontSize: '0.52rem', opacity: 0.6 }} />
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
};
